// File: src/data/routingLearningPath.js
// Content for the "Learning to Route" course page. The course material is
// Helia's own teaching board on deep learning for combinatorial optimization. The evidence notes come from the September 2026 fleet-routing
// research report. The tag field on each item records how well the claim was verified in
// that run and is kept for maintenance; it is not shown on the page.

const REPORT = "/ai-reports/state-of-ai-in-logistics";

export const anchors = {
  report: REPORT,
  onePage: `${REPORT}#the-field-in-one-page`,
  timeline: `${REPORT}#timeline`,
  thread1: `${REPORT}#thread-1-learned-solvers-versus-metaheuristics-from-replace-`,
  thread2: `${REPORT}#thread-2-the-model-writes-the-heuristic-from-funsearch-to-an`,
  thread3: `${REPORT}#thread-3-routing-from-a-fleet-s-own-history-the-winner-did-n`,
  thread4: `${REPORT}#thread-4-modeling-the-day-dynamic-stochastic-rich-real-time`,
  thread5: `${REPORT}#thread-5-the-matrix-learned-eta-at-platforms-guessed-matrice`,
  builder: `${REPORT}#a-builder-s-minimum-viable-system-from-the-evidence`,
  reading: `${REPORT}#reading-list-ranked-ten-sources-worth-opening-and-why`,
  beliefs: `${REPORT}#what-the-field-believes-now-that-it-did-not-in-2019`,
};

export const sections = [
  { id: "problem", num: "01", title: "The problem: a tour, then a fleet" },
  { id: "local-search", num: "02", title: "Local search and metaheuristics" },
  { id: "supervised", num: "03", title: "Supervised learning: copy the optimal tour" },
  { id: "sl-limits", num: "04", title: "Where supervised learning breaks" },
  { id: "rl", num: "05", title: "Reinforcement learning: state, action, reward, value" },
  { id: "explore", num: "06", title: "Exploration versus exploitation, on and off policy" },
  { id: "rl-limits", num: "07", title: "Where direct reinforcement learning breaks" },
  { id: "rl-inside", num: "08", title: "Learning inside the metaheuristic" },
  { id: "gnn", num: "09", title: "Where graph networks and transformers sit" },
  { id: "2026", num: "10", title: "What the 2026 evidence adds" },
];

// Evidence notes per section. Each item: { tag, text, link? }.
export const evidence = {
  problem: {
    intro:
      "The course used the travelling salesman problem because it fits on one sticky note. Fleets solve the vehicle routing problem (VRP): many vehicles, capacities, time windows, and a travel-time matrix that is guessed or bought.",
    items: [
      {
        tag: "HIGH",
        text: "On real road matrices from an operator (EURO Meets NeurIPS 2022, ORTEC grocery instances, 200 to 900 customers) the top three static solvers finished within 0.016% of each other. The organizers wrote that the instances were too small to permit improvements over the HGS baseline.",
        link: "thread4",
      },
      {
        tag: "HIGH",
        text: "Amazon city-center van drivers spend two-thirds of their time parking or walking (Schrader et al. 2024). Parking search averages 5.8 minutes per stop in Seattle and 24 in New York. Against those magnitudes the gap between a good and a great tour is noise.",
        link: "thread4",
      },
      {
        tag: "computed",
        text: "On the Molde planned-versus-driven dataset (19,647 routes, 400 drivers) 76.7% of routes were driven in a different order than planned, and the driven routes were 1.5% shorter than the plan in geodesic distance. Drivers deviate constantly and slightly better than the plan.",
        link: "thread3",
      },
    ],
    more: "The abstract TSP is the right place to learn the mechanics. Keep in mind that the thing worth optimizing in a fleet is rarely the tour length.",
  },
  "local-search": {
    intro:
      "The loop on the board is the skeleton of every serious routing solver. Hybrid genetic search (HGS) adds a population and a crossover on top of exactly this loop. PyVRP was HGS until January 2026 and is now iterated local search, the same loop with restarts.",
    items: [
      {
        tag: "HIGH",
        text: "On the standard X benchmark, HGS reaches a 0.29% gap to best-known in 111 seconds. The best neural constructive solver in the same February 2026 survey sits at 4.10%, and the survey authors, who build the top neural solvers, wrote that many neural solvers are outperformed by nearest-neighbour construction.",
        link: "thread1",
      },
      {
        tag: "computed",
        text: "On one Apple M1 Pro core, PyVRP 0.14 sits within 0 to 1% of best-known at 60 seconds up to 500 customers and at 1.4% at 1,000 customers. OR-Tools guided local search averaged 5% at 60 seconds and stalled on instances of 700 or more customers.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "Four of the top five CVRP solvers in the 2022 DIMACS challenge and the VRPTW winner were HGS derivatives. The winning static code of the 2022 EURO-NeurIPS competition became PyVRP (MIT licence, 254,609 PyPI downloads a month in September 2026).",
        link: "thread1",
      },
    ],
    more: "The question the rest of this course asks is not whether to replace this loop. It is which decisions inside the loop are worth learning.",
  },
  supervised: {
    intro:
      "The board's supervised recipe is the 2015 Pointer Network: attention over the input cities becomes the output, trained by imitating optimal tours. The heat-map idea on the board became a whole research line.",
    items: [
      {
        tag: "HIGH",
        text: "Pointer Networks (Vinyals, Fortunato, Jaitly, Google Brain, June 2015) were trained by supervised imitation of optimal or heuristic tours on planar TSPs of 5 to 50 nodes. Every constructive neural solver since is a pointer network with a better encoder and a better training signal.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "Joshi, Laurent and Bresson (2019) trained a graph convolutional network with supervised edge labels from Concorde, a heat map of edges, and decoded with beam search: 1.39% gap at TSP100 in 40 minutes.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "LEHD and BQ-NCO (2023) moved the capacity into a heavy decoder trained by imitation on optimal tours at 100 nodes and hold under 5% at 1,000 nodes. FM-MCVRP, a 206-million-parameter transformer trained on 38.1 million HGS-labelled instances, lands within about 1% of HGS at 5 seconds.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "The heat map inside a real solver was tested by the HGS author himself. Santana, Lodi and Vidal (2023) put GNN edge heat maps into HGS's crossover and local search and titled the paper 'A Possible Overkill?': heat maps did not present significant advantages over simpler distance measures.",
        link: "thread1",
      },
    ],
    more: "The course's intuition (use the model's knowledge to guide search, not to replace it) is the one idea from this era that survived. The specific form (edge heat maps) did not beat plain nearness measures inside a strong solver.",
  },
  "sl-limits": {
    intro:
      "Every limitation on the board was later measured. The generalization failure became the organizing problem of the field for five years.",
    items: [
      {
        tag: "HIGH",
        text: "A POMO model trained at 100 nodes is 128.9% off at 1,000 nodes. Joshi, Cappart, Rousseau and Laurent (2021): learned TSP policies perform closely to classical solvers when trained on trivially small sizes and are unable to generalize to larger instances.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "A NeurIPS 2025 paper showed that POMO, LEHD and BQ overfit the capacity constraint: gaps widen 5.5x to 9.3x when capacity moves from 50 to 10 or 500. This is the board's 'conditions differ from historical trends' point with numbers.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "FrontierCO (CMU, ICLR 2026) built a benchmark from real and hard instances. LEHD reports a 0.72% gap on the standard TSP benchmark; on real instances the gap widens to 10% on easy ones and 77% on hard ones.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "The board's 'train small, split big, keep local information' escape hatch is now the large-scale default: GLOP, UDC and L2R partition a 100,000-node problem into pieces a small model can solve. Learning to Delegate (MIT, 2021) learns which sub-problem to hand to LKH-3 or HGS and reaches equal quality 10x to 100x faster on 500 to 3,000 customers.",
        link: "thread1",
      },
    ],
    more: "One thing the board predicted and the field confirmed: the leading young builders of the construction line (Berto, Joshi) left routing for biology in 2026. The idea of a pure supervised solver is published but no longer believed by its own authors.",
  },
  rl: {
    intro:
      "The board's story of You and the Model is Q-learning told as a conversation. The RL history on the board runs 1989, 1995, 2017. The research report adds dates for the routing side.",
    items: [
      {
        tag: "LOW",
        text: "The board's 1989 is Watkins's Q-learning thesis and 1995 is Zhang and Dietterich's RL for job-shop scheduling; both are from memory and were not verified in the research run.",
      },
      {
        tag: "HIGH",
        text: "Bello, Pham, Le, Norouzi and Bengio (Google Brain, November 2016) replaced supervision with REINFORCE using negative tour length as reward, added a critic baseline, and named the field 'neural combinatorial optimization'. The board's 'get deep' step.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "Kool, van Hoof and Welling (ICLR 2019) made the encoder a transformer and used the policy's own greedy rollout as the REINFORCE baseline. CVRP100 within 3.72% of LKH-3 after 2 hours of sampling. POMO (Samsung SDS, NeurIPS 2020) used the symmetry of a tour as a multi-start baseline: 0.69% at CVRP100 in one minute for 10,000 instances.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "Kool's own verdict in his 2022 thesis: the methods have not provided an immediate and unconditional improvement of the state of the art for vehicle routing. He went on to co-author PyVRP.",
        link: "thread1",
      },
    ],
    more: "The value the model estimates on the board is the same object DoorDash and Optimal Dynamics use in production, only there it estimates the value of a dispatch decision, not the next city.",
  },
  explore: {
    intro:
      "The trade-off on the board is real in production. The 2022 competition is the cleanest measurement of how much anticipation and learning are worth in dispatch.",
    items: [
      {
        tag: "HIGH",
        text: "EURO Meets NeurIPS 2022, dynamic track, hidden final instances: greedy dispatch 399,725; a no-learning scenario sampler 10.12% better than greedy; the learned winner Kleopatra 12.73% better; the organizers' own DQN equal to greedy. Anticipation is worth 10 to 13 points; learning versus sampling about 3.",
        link: "thread4",
      },
      {
        tag: "HIGH",
        text: "Kleopatra learned a prize per open order (how costly it is to postpone) with a ten-neuron-wide network and let a prize-collecting HGS decide the routes. ICD, a training-free sampling method from the PyVRP authors, got within 0.8% of it at 120 seconds per epoch.",
        link: "thread4",
      },
      {
        tag: "HIGH",
        text: "The board's Monte Carlo tree search sticky has a 2026 descendant: LaF-MCTS (NTU and A*STAR, May 2026) runs LLM-assisted tree search over a decomposition-plus-HGS architecture, 1.10 to 1.39% gap on CVRPLIB 600 to 1,000 customers. Its HGS baseline is 4 to 8x worse than HGS-CVRP's published gaps, so read with care.",
        link: "thread2",
      },
    ],
    more: "For a fleet with a decent solver, sample scenarios before training anything. The learned policy buys about 3 points on top, and only with real data to train on.",
  },
  "rl-limits": {
    intro:
      "The board's three limitations (constraints, action space, an unnecessary self-imposed rule) map onto how the field actually turned.",
    items: [
      {
        tag: "HIGH",
        text: "Constraints: Team SB at EURO-NeurIPS 2022 (Samsung SDS, with Hottung) reported that end-to-end neural construction sat 5% behind HGS on real constrained instances and abandoned it. NeuOpt (2023) lets the search pass through infeasible solutions with reward shaping instead of masking, the board's 'limit the action set' with a softer edge.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "In 2026 the LLM's real job with constraints is the modeler, not the policy: VRPCoder (an 8B model) reaches 93% pass@1 over 21 VRP variants at turning natural language into a correct constraint model, with probes that catch silently dropped constraints.",
        link: "thread4",
      },
      {
        tag: "HIGH",
        text: "Action space: the board's 'do clustering first, then sub-problems' is the partition-and-conquer line (GLOP, UDC, L2R at 100K nodes) and Learning to Delegate. The board's hierarchical RL and deferred decisions are how NLNS and NDS split 'where to look' from 'what move to apply'.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "The unnecessary limitation: a heuristic tries hundreds of complete solutions while direct RL builds one. The field's answer is Neural Deconstruction Search (Bielefeld, TMLR 2025): learn only which part of the solution to destroy, rebuild with greedy insertion and local search. It beats HGS by 0.90% at 1,000 customers and 2.34% at 2,000 at equal wall-clock, with one caveat that decides everything: HGS had one CPU core, NDS also had an A100.",
        link: "thread1",
      },
    ],
    more: "Every result that beats HGS at equal time keeps a human-built search skeleton and learns one decision inside it.",
  },
  "rl-inside": {
    intro:
      "The board's 'RL guiding local search' (choose which small change to make) is the branch that produced the durable results, including the 2025 turn where the learner writes the operator's code.",
    items: [
      {
        tag: "HIGH",
        text: "NeuRewriter (Facebook AI, 2019) learned a region picker and a rule picker. NLNS (Bielefeld, 2020) learned the repair step of large-neighbourhood search. NDS (2025) inverted it and learned the destroy step. NeuroLKH (NTU, 2021) kept LKH intact and learned only its candidate edge set. All four are 'operator selection' and 'edge selection' from the board.",
        link: "thread1",
      },
      {
        tag: "HIGH",
        text: "VRPAgent (Bielefeld and KAIST, October 2025): an LLM writes the destroy and repair operator pair of a large-neighbourhood search and a genetic algorithm selects among operators. On synthetic 1,000 and 2,000 customer instances it is 1 to 4% better than HGS at 120 to 240 seconds on one CPU core. Cost of one discovery run: $1 to $19.",
        link: "thread2",
      },
      {
        tag: "HIGH",
        text: "AlphaEvolve (DeepMind, May 2025) put a five-line evolved scoring function into Google's Borg scheduler, recovering 0.7% of fleet-wide compute. It was chosen over deep RL for interpretability, debuggability, predictability and ease of deployment. The learning moved from the policy to the operator code.",
        link: "thread2",
      },
      {
        tag: "HIGH",
        text: "The board's 'adjust the hyperparameters of the metaheuristic' is the one learning system a European routing vendor showed at VeRoLog 2025: OptiTune (OPTIT, Bologna) learns solver parameters from client history.",
        link: "thread2",
      },
      {
        tag: "HIGH",
        text: "The board's inference-cost warning holds. The 2023 norm 'ML must run at OR time budgets' (Routing Arena) and FrontierCO's one-hour budgets both exist because slow good decisions lost to fast worse ones. NDS needs a GPU to win at equal wall-clock.",
        link: "thread1",
      },
    ],
    more: "Whoever owns the evaluator owns the result: 64 instances at 20 seconds (VRPAgent), a datacenter simulator on historical snapshots (Borg), 60 recorded warehouse tours (FM Logistic). For a fleet, the evaluator is its own executed-route history.",
  },
  gnn: {
    intro:
      "The GNN branch of the board (introduction, power, limitations, transformers) is where the encoders in every model above come from. Its biggest production win in routing is not in the solver.",
    items: [
      {
        tag: "HIGH",
        text: "Google Maps ETA with graph neural networks (DeepMind and Google, CIKM 2021): the road network is cut into supersegments, a GNN predicts travel time at several horizons. Online it cut RMSE against the historical baseline by 23% in New York and 27% in Tokyo. For a plan built at 06:00 for legs at 14:00, the hour-of-day profile beats the live feed and the GNN beats both by 10 to 25%.",
        link: "thread5",
      },
      {
        tag: "HIGH",
        text: "Baidu's own baseline (average speed per link per 5-minute weekly slot, summed along the route) is roughly twice as wrong as its learned model on the same routes: MAPE 40 to 54% against 20 to 27%.",
        link: "thread5",
      },
      {
        tag: "HIGH",
        text: "Meituan's production dispatch (INFORMS Journal on Applied Analytics, 2024) uses a GNN only to score candidate order combinations and inverse RL only to learn the route objective from courier behaviour. The assignment itself is a classical optimizer called 48.3 million times a minute at lunch.",
        link: "thread4",
      },
    ],
    more: "The transformer encoder in Kool's 2019 model is the same object as the GNN sessions' 'more, e.g. transformers' sticky: attention over a set of cities with no fixed order.",
  },
  "2026": {
    intro:
      "The course asked how to learn the solver. The 2026 report's answer is that the field spent a decade trying to learn the solver and has just started learning the inputs.",
    items: [
      {
        tag: "HIGH",
        text: "Learning from a fleet's own history was tested once in public (Amazon Last Mile Routing Research Challenge, 2021, 9,184 real routes). The winner did not train a model: parse Amazon's zone hierarchy, copy precedence from the single most similar historical route, run a penalty-based LKH. Score 0.0248 against 0.0703 for the travel-time-optimal tour; every deep or RL entry scored 0.059 to 0.92.",
        link: "thread3",
      },
      {
        tag: "HIGH",
        text: "The one experiment that tests amortization over a territory directly (Thyssens et al., Hildesheim, 2025): a POMO trained on sub-samples of one fixed 10,000-node clustered territory beats HGS-CVRP by 2.56 to 3.94% at 0.7 to 50 seconds, while the same recipe on a uniform base loses by 1.7 to 5.6%. Synthetic geography; nobody has run it on a real fleet's instance distribution.",
        link: "thread1",
      },
      {
        tag: "computed",
        text: "A static travel-time matrix is a punctuality error, not a cost error. On the Bonn time-dependent instances (500 customers, ten cities) plans built on a time-averaged matrix cost only 2.2% more than the time-dependent best-known but leave 4.1% of all stops and 8.3% of tight-window stops late. The only static way to be on time costs 13% more.",
        link: "thread5",
      },
      {
        tag: "HIGH",
        text: "Not one commercial routing vendor page, engineering blog, patent or job posting read in the run describes a learned construction or improvement policy inside the optimizer. Learning lives in service times, ETAs and geocoding. The architecture is a 1990s engine with a learned ETA on top.",
        link: "onePage",
      },
    ],
    more: "Predict, Plan, Execute, Learn: the course is about the Plan stage. The evidence says spend the learning budget on Predict (service time, parking, failure probability, the matrix), on what to dispatch, and on the Learn stage that no public dataset fully supports yet.",
  },
};

export const glossary = [
  ["Tour", "One closed route that visits every stop exactly once and returns to the start."],
  ["TSP", "Travelling salesman problem: shortest tour over N cities given the distances between them."],
  ["VRP", "Vehicle routing problem: several vehicles, capacities, time windows and a depot. What fleets actually solve."],
  ["Local search", "Start from a solution, change something small, keep it if it helps, repeat until tired."],
  ["Metaheuristic", "A strategy that steers local search (restarts, populations, acceptance of worse moves) so it escapes local optima."],
  ["HGS", "Hybrid genetic search (Vidal). A population of solutions, crossover, then local search on each child. The reference routing solver since 2020."],
  ["PyVRP", "Open-source Python solver by the EURO-NeurIPS 2022 winners. HGS until version 0.12, iterated local search from 0.13 (January 2026)."],
  ["LKH-3", "Helsgaun's Lin-Kernighan implementation; the reference TSP heuristic and the neural papers' favourite baseline."],
  ["Gap", "How far a solution's cost is above the best-known solution, in percent. The number every solver claim should carry, at a stated time budget."],
  ["Supervised learning", "Input plus expected output; the model is corrected toward the label."],
  ["Loss", "The measured difference between what the model produced and what the label said."],
  ["Heat map", "A model's estimate, per edge, of how likely that edge is in the optimal tour. Used to guide search."],
  ["Reinforcement learning", "No labels. The model acts, an environment returns a reward, the model corrects its estimate of how good each action was."],
  ["State", "Everything the model can see when it decides: here the cities, the distances and the partial tour so far."],
  ["Action", "One decision: which city to add next, or which operator to apply."],
  ["Reward", "The number the environment returns after an action. On the board: minus the distance just travelled."],
  ["Session", "The board's word for an episode: one full sequence of decisions from start to termination."],
  ["Value", "The sum of rewards the model expects from here to the end of the session."],
  ["Policy", "The rule that turns a state into an action, deterministic or probabilistic."],
  ["Off-policy", "Learn the value of the best action while behaving differently (for instance exploring). Q-learning."],
  ["On-policy", "Learn the value of the policy you actually follow, including its exploration. The model owns the trade-off."],
  ["MCTS", "Monte Carlo tree search: simulate many futures from the current state using the policy's probabilities, then take the action that did best."],
  ["Operator", "A named small change: 2-opt reverses a segment, swap exchanges two stops, relocate moves one stop."],
  ["Destroy and repair", "Large-neighbourhood search: remove part of the solution, reinsert it better. The step the 2025 learned solvers learn."],
  ["Time budget", "The wall-clock a solver is allowed. Every comparison is meaningless without it."],
  ["Travel-time matrix", "N by N travel times between stops, ideally per departure time. Guessed or bought; the biggest unpriced input."],
];

// Course timeline (from the board) merged with report-dated events.
export const timeline = [
  { yr: "1989", course: true, tag: "LOW", text: "Reinforcement learning as the board dates it. The usual reference is Watkins's Q-learning thesis (from memory, not verified in the research run)." },
  { yr: "1995", course: true, tag: "LOW", text: "RL applied to combinatorial optimization as the board dates it. The usual reference is Zhang and Dietterich on job-shop scheduling (from memory, not verified)." },
  { yr: "2015", tag: "HIGH", text: "Pointer Networks (Google Brain): attention over the input becomes the output. Supervised on optimal tours of 5 to 50 cities." },
  { yr: "2016", course: true, tag: "HIGH", text: "Bello et al. add REINFORCE and name neural combinatorial optimization. The board's 'get deep' step, dated 2017 there; the arXiv preprint is November 2016." },
  { yr: "2017", course: true, tag: "LOW", text: "GNN plus RL for combinatorial optimization, as the board dates it. The usual reference is Khalil and Dai et al. at NeurIPS 2017 (from memory, not verified)." },
  { yr: "2019", tag: "HIGH", text: "Kool, van Hoof, Welling, 'Attention, Learn to Solve Routing Problems!': transformer encoder plus greedy-rollout baseline. CVRP100 within 3.72% of LKH-3." },
  { yr: "2019", tag: "HIGH", text: "NeuRewriter and NLNS: learned components inside iterative search. Quietly, the more durable idea." },
  { yr: "2020", tag: "HIGH", text: "POMO: symmetry as a multi-start baseline, 0.69% at CVRP100. The high-water mark of 'solved at 100'. Same year Vidal releases HGS-CVRP." },
  { yr: "2021", tag: "HIGH", text: "The generalization reckoning: trained at 100, 128.9% off at 1,000. Amazon Last Mile Routing Research Challenge: won by rules plus LKH, not by a neural net." },
  { yr: "2022", tag: "HIGH", text: "EURO Meets NeurIPS: static track saturates on real matrices; dynamic track won by a learned prize inside HGS. Kool's thesis verdict. DIMACS won by HGS derivatives." },
  { yr: "2023", tag: "HIGH", text: "LEHD and BQ hold under 5% at 1,000. Santana, Lodi, Vidal test heat maps inside HGS: 'A Possible Overkill?'. FunSearch: an LLM as the mutation operator." },
  { yr: "2025", tag: "HIGH", text: "Neural Deconstruction Search beats HGS at equal wall-clock with a GPU. AlphaEvolve ships an evolved scoring function in Borg. VRPAgent: LLM-written operators inside a human-built LNS." },
  { yr: "2026", tag: "HIGH", text: "The audit year: the field's own survey puts HGS at 0.29% and the best neural solver at 4.10% on Set-X. FrontierCO shows neural gaps of 10 to 77% on real instances. PyVRP stops being HGS." },
];

export const readingList = [
  ["Kool, van Hoof, Welling, 'Attention, Learn to Solve Routing Problems!' (ICLR 2019) and Kool's 2022 thesis", "The reference RL construction model, and its author's honest verdict three years later."],
  ["Joshi, Cappart, Rousseau, Laurent, 'Learning TSP Requires Rethinking Generalization' (CP 2021)", "The controlled experiment behind every 'does not generalize' sentence in this course."],
  ["Santana, Lodi, Vidal, 'Neural Networks for Local Search and Crossover in Vehicle Routing: A Possible Overkill?' (CPAIOR 2023)", "The heat-map idea tested inside the solver that matters, by the solver's author."],
  ["Hottung, Wong-Chung, Tierney, 'Neural Deconstruction Search' (TMLR 2025)", "Learning one decision inside search; the equal-wall-clock win with the hardware caveat written down."],
  ["Kool, Lan, Wouda, Zhang et al., 'The EURO Meets NeurIPS 2022 Vehicle Routing Competition' (PMLR v220)", "Real matrices, static saturation, and the value of anticipation versus learning in dispatch."],
  ["Ba, Lin, Zhou, Wang, Zhang et al., neural routing solver survey (arXiv 2602.21761, February 2026), Table IX", "The field grading itself on Set-X."],
  ["Cook, Held, Helsgaun, 'Constrained local search for last-mile routing' (Transportation Science 2024)", "The only public 'learn from history' win, with zero fitted parameters."],
];
